import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  CanvasGraphModel,
  CanvasManualAdjustmentModel,
  Schema,
  apiService,
} from '../../services/api';
import {
  toRecord,
  parseTimestamp,
  normaliseIdentifier,
  arrayToInputString,
} from '../../utils/dataUtils';
import type { DiffResult, ReviewComment } from './types';

export interface DraftWorkspaceDataParams {
  schemaId: string | undefined;
  diffSchemaId: string;
  promoteSchemaId: string;
  setPromoteSchemaId: (value: string) => void;
  setDiffSchemaId: (value: string) => void;
  setPromoteSchemaName: (value: string) => void;
  promoteSchemaIdCurrent: string;
  diffSchemaIdCurrent: string;
  promoteSchemaNameCurrent: string;
  schemaFromContext: { schema_id?: string; schema_name?: string; name?: string; status?: string } | null;
}

export function useDraftWorkspaceData(params: DraftWorkspaceDataParams) {
  const {
    schemaId,
    diffSchemaId,
    promoteSchemaId,
    setPromoteSchemaId,
    setDiffSchemaId,
    setPromoteSchemaName,
    promoteSchemaIdCurrent,
    diffSchemaIdCurrent,
    promoteSchemaNameCurrent,
    schemaFromContext,
  } = params;

  const draftQuery = useQuery<Schema>(
    ['schema', schemaId],
    async () => {
      const result = await apiService.getSchema(schemaId!);
      if (!result) throw new Error(`Schema ${schemaId} not found`);
      return result;
    },
    {
      enabled: Boolean(schemaId),
      onSuccess: (draft) => {
        const resolvedSchemaId =
          draft.schema_id ?? schemaFromContext?.schema_id ?? '';
        if (!promoteSchemaIdCurrent && resolvedSchemaId) {
          setPromoteSchemaId(resolvedSchemaId);
        }
        if (!diffSchemaIdCurrent && resolvedSchemaId) {
          setDiffSchemaId(resolvedSchemaId);
        }
        if (!promoteSchemaNameCurrent) {
          const suggested =
            (draft.schema_name as string | undefined) ??
            (draft.definition?.name as string | undefined) ??
            '';
          setPromoteSchemaName(suggested);
        }
      },
    }
  );

  const schemaSetQuery = useQuery<Schema>(
    ['schema', diffSchemaId],
    async () => {
      const result = await apiService.getSchema(diffSchemaId);
      if (!result) throw new Error(`Schema ${diffSchemaId} not found`);
      return result;
    },
    {
      enabled: Boolean(diffSchemaId),
    }
  );

  const draft = draftQuery.data;

  const ledgerWorkbookId = useMemo(() => {
    const targetSchemaId = draft?.schema_id ?? promoteSchemaId.trim();
    return targetSchemaId || '';
  }, [draft, promoteSchemaId]);

  const ledgerQuery = useQuery<CanvasGraphModel>(
    ['schema-ledger', ledgerWorkbookId],
    () => apiService.getCanvasGraph(ledgerWorkbookId),
    {
      enabled: Boolean(ledgerWorkbookId),
      refetchOnWindowFocus: false,
    }
  );

  const schemaSummary = useMemo(() => {
    if (!draft) {
      return null;
    }
    const definition = draft.definition ?? {};
    const tables = definition.tables ? Object.keys(definition.tables) : [];
    return {
      tableCount: tables.length,
      suggestedName:
        (draft.schema_name as string | undefined) ??
        (definition.name as string | undefined) ??
        draft.metadata?.schema_name ??
        '',
      status: draft.status,
      createdAt: draft.created_at,
    };
  }, [draft]);

  const enrichmentMetadata = useMemo(
    () => toRecord(toRecord(draft?.metadata ?? {}).enrichment),
    [draft]
  );

  const tableEnrichmentMap = useMemo(
    () => toRecord(enrichmentMetadata.tables),
    [enrichmentMetadata]
  );

  const fieldEnrichmentMap = useMemo(
    () => toRecord(enrichmentMetadata.fields),
    [enrichmentMetadata]
  );

  const schemaEnrichmentSnapshot = useMemo(
    () => ledgerQuery.data?.metadata?.schema_enrichment ?? null,
    [ledgerQuery.data]
  );

  const tableSnapshotMap = useMemo(
    () => toRecord(schemaEnrichmentSnapshot?.tables),
    [schemaEnrichmentSnapshot]
  );

  const fieldSnapshotMap = useMemo(
    () => toRecord(schemaEnrichmentSnapshot?.fields),
    [schemaEnrichmentSnapshot]
  );

  const schemaEnrichmentVersion = schemaEnrichmentSnapshot?.version ?? null;

  const reviewComments = useMemo<ReviewComment[]>(() => {
    const metadataRecord = toRecord(draft?.metadata);
    const commentsRaw = metadataRecord.review_comments;
    if (!Array.isArray(commentsRaw)) {
      return [];
    }
    const comments: ReviewComment[] = commentsRaw
      .map((entry) => {
        const record = toRecord(entry);
        const commentId = String(record.comment_id ?? '').trim();
        const message = String(record.message ?? '').trim();
        if (!commentId || !message) {
          return null;
        }
        return {
          comment_id: commentId,
          author: String(record.author ?? 'unknown').trim() || 'unknown',
          message,
          created_at: String(record.created_at ?? record.timestamp ?? '').trim(),
        };
      })
      .filter((entry): entry is ReviewComment => Boolean(entry));
    return comments.sort(
      (left, right) => parseTimestamp(right.created_at) - parseTimestamp(left.created_at)
    );
  }, [draft]);

  const schemaAdjustments = useMemo<CanvasManualAdjustmentModel[]>(() => {
    const adjustments = ledgerQuery.data?.metadata?.manual_adjustments ?? [];
    return adjustments.filter(
      (entry) => entry.target_kind === 'schema_table' || entry.target_kind === 'schema_field'
    );
  }, [ledgerQuery.data]);

  const sortedSchemaAdjustments = useMemo<CanvasManualAdjustmentModel[]>(() => {
    if (!schemaAdjustments.length) {
      return [];
    }
    return [...schemaAdjustments].sort(
      (left, right) =>
        parseTimestamp(right.created_at) - parseTimestamp(left.created_at)
    );
  }, [schemaAdjustments]);

  const tablesDefinition = useMemo(() => {
    if (!draft) {
      return {};
    }
    const definition = toRecord(draft.definition);
    return toRecord(definition.tables);
  }, [draft]);

  const tableNames = useMemo(() => Object.keys(tablesDefinition), [tablesDefinition]);

  const diffSummary = useMemo(() => (diffResult: DiffResult | null) => {
    if (!diffResult) {
      return null;
    }
    const summary = diffResult.summary ?? {};
    const tablesAdded = Array.isArray(diffResult.tables_added) ? diffResult.tables_added.length : 0;
    const tablesRemoved = Array.isArray(diffResult.tables_removed) ? diffResult.tables_removed.length : 0;
    const tablesChanged = Array.isArray(diffResult.tables_changed) ? diffResult.tables_changed.length : 0;
    return {
      ...summary,
      tablesAdded,
      tablesRemoved,
      tablesChanged,
    };
  }, []);

  const schemaSetTableNames = schemaSetQuery.data?.definition?.tables
    ? Object.keys(schemaSetQuery.data.definition.tables)
    : [];
  const selectedSchemaSetName =
    schemaSetQuery.data?.schema_name ?? (schemaFromContext ? schemaFromContext.schema_name : undefined);
  const selectedSchemaSetStatus =
    schemaSetQuery.data?.status ?? schemaFromContext?.status;

  return {
    draftQuery,
    schemaSetQuery,
    ledgerQuery,
    draft,
    ledgerWorkbookId,
    schemaSummary,
    enrichmentMetadata,
    tableEnrichmentMap,
    fieldEnrichmentMap,
    schemaEnrichmentSnapshot,
    tableSnapshotMap,
    fieldSnapshotMap,
    schemaEnrichmentVersion,
    reviewComments,
    schemaAdjustments,
    sortedSchemaAdjustments,
    tablesDefinition,
    tableNames,
    computeDiffSummary: diffSummary,
    schemaSetTableNames,
    selectedSchemaSetName,
    selectedSchemaSetStatus,
  };
}
